import { addMoney, subtractMoney } from '@shared/lib/money';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
	CheckinSaveOrderServicesMutation,
	CheckinSaveOrderServicesMutationVariables,
	InsuranceProgramFragment,
	OrderDocumentType,
	SaveOrderServicesMutation,
	SaveOrderServicesMutationVariables,
	useCheckinSaveOrderServicesMutation,
	useOrderDocumentQuery,
	useSaveOrderServicesMutation
} from '@websky/graphql';
import {
	getAvailableInsuranceCodesByPrograms,
	getAvailableSelectedInsuranceCodes,
	getImportantInsuranceCodes as getCheckoutImportantInsuranceCodes,
	getOrder as getCheckoutOrder,
	getOrderId as getCheckoutOrderId
} from '../Checkout/store/order/selectors';
import {
	getInsurancePriceToPay as getCheckoutInsurancePriceToPay,
	getTotalPriceToPay as getCheckoutTotalPriceToPay
} from '../Checkout/store/orderPrice/selectors';
import { fillOrder } from '../Checkout/store/order/actions';
import { Mode, Money as MoneyType } from '../types';
import { Currency } from '../enums';
import {
	getCheckinOrder,
	getImportantInsuranceCodes as getCheckinImportantInsuranceCodes,
	getInsurancePrice as getCheckinInsurancePriceToPay,
	getTotalPriceToPay as getCheckinTotalPriceToPay,
	getCheckinOrderId
} from '../Modules/Checkin/store/order/selectors';
import { fillCheckinOrder } from '../Modules/Checkin/store/order/actions';
import { getSelectedInsuranceSaveCodes } from './utils';
import { saveOrderServicesParamsFactory } from './store/utils';
import { useInsuranceUpsaleModal } from './components/InsuranceUpsaleModal/InsuranceUpsaleModal.hook';
import { UpsaleStrategy } from './types';
import { ModeContext, useConfig } from '../context';
import { get, IMPORTANT_INSURANCE_TOUCHED, sessionStorageGet, sessionStorageSet, set } from '../cache';
import { useToggleable } from '../hooks';
import { downloadPDF } from '../utils';

interface UseInsurancePageWrapperProps {
	strategy: UpsaleStrategy;
	insurancePrograms?: InsuranceProgramFragment[];
}

export const useInsurancePageWrapper = ({ strategy, insurancePrograms }: UseInsurancePageWrapperProps) => {
	const dispatch = useDispatch();
	const mode = useContext(ModeContext);

	const order = mode === Mode.Checkin ? useSelector(getCheckinOrder) : useSelector(getCheckoutOrder);
	const orderPriceToPay =
		mode === Mode.Checkin ? useSelector(getCheckinTotalPriceToPay) : useSelector(getCheckoutTotalPriceToPay);
	const orderInsuranceToPay =
		mode === Mode.Checkin
			? useSelector(getCheckinInsurancePriceToPay)
			: useSelector(getCheckoutInsurancePriceToPay);

	// доступные коды страховок
	const availableInsuranceCodes = useSelector(getAvailableInsuranceCodesByPrograms(insurancePrograms));

	// выбранные коды страховок
	const selectedInsuranceCodes = useSelector(getAvailableSelectedInsuranceCodes(availableInsuranceCodes));

	// другие выбранные коды страховок (вне группы)
	const otherSelectedInsuranceCodes = useSelector(getAvailableSelectedInsuranceCodes(availableInsuranceCodes, false));

	const importantInsurances = useImportantInsurances({
		availableInsuranceCodes,
		selectedInsuranceCodes
	});

	const {
		global: {
			insurance: { allowImplicitConfirm }
		}
	} = useConfig();

	const [isNeedToSendSaveRequest, setIsNeedToSendSaveReq] = useState(false);

	const {
		isOpen: isShowImportantInsurancesError,
		open: onShowImportantInsurancesError,
		close: onHideImportantInsurancesError
	} = useToggleable(false);

	const [saveOrderServices, mutationResult] =
		mode === Mode.Checkin ? useCheckinSaveOrderServicesMutation() : useSaveOrderServicesMutation();
	const { refetch: getDocument } = useOrderDocumentQuery({ skip: true });

	const [saveOrderServicesParams, setSaveOrderServicesParams] = useState<
		SaveOrderServicesMutationVariables | CheckinSaveOrderServicesMutationVariables
	>();

	const selectedSaveServicesCodes = useMemo(() => {
		return getSelectedInsuranceSaveCodes(saveOrderServicesParams?.params?.insurances, availableInsuranceCodes);
	}, [saveOrderServicesParams?.params?.insurances, availableInsuranceCodes]);

	const insuranceCodesToSave = useMemo(() => {
		return [...new Set([...otherSelectedInsuranceCodes, ...selectedSaveServicesCodes])];
	}, [otherSelectedInsuranceCodes, selectedSaveServicesCodes]);

	const insuranceUpsaleModal = useInsuranceUpsaleModal({
		strategy,
		availableInsuranceCodes,
		selectedInsuranceCodes: insuranceCodesToSave
	});

	useEffect(() => {
		if (mode === Mode.Checkin) {
			if ((mutationResult?.data as CheckinSaveOrderServicesMutation)?.CheckinSaveOrderServices) {
				dispatch(
					fillCheckinOrder((mutationResult.data as CheckinSaveOrderServicesMutation).CheckinSaveOrderServices)
				);
			}

			return;
		}

		if ((mutationResult?.data as SaveOrderServicesMutation)?.SaveOrderServices) {
			dispatch(fillOrder((mutationResult.data as SaveOrderServicesMutation).SaveOrderServices));
		}
	}, [mutationResult?.data]);

	const [insurancePriceToPay, setInsurancePriceToPay] = useState<MoneyType>({
		amount: 0,
		currency: Currency.RUB
	});

	const priceWithoutInsurance = subtractMoney(orderPriceToPay, orderInsuranceToPay);

	const totalPriceToPay = addMoney(priceWithoutInsurance, insurancePriceToPay);

	const isNeedSelectImportantInsurances = useMemo(() => {
		if (allowImplicitConfirm) {
			return false;
		}

		return !importantInsurances.isAllImportantInsurancesTouchedOrSelected;
	}, [allowImplicitConfirm, importantInsurances.isAllImportantInsurancesTouchedOrSelected]);

	const saveOrderServicesWithParams = useCallback(
		(codes: string[]) => {
			const availableTravellersIdSet = new Set<string>();

			order.additionalServices.insurances?.insurancePrograms?.forEach(insurance => {
				if (codes.includes(insurance.code)) {
					insurance.allowedPassengers?.forEach(id => availableTravellersIdSet.add(id.toString()));
				}
			});

			const travellerIds = (order.travellers as { id: string }[])
				.filter(traveller => availableTravellersIdSet.has(traveller.id))
				.map(traveller => traveller.id);

			const saveOrderParams = saveOrderServicesParamsFactory(travellerIds, codes);

			return saveOrderServices({
				variables: {
					params: {
						...saveOrderParams.params,
						id: order.id
					}
				}
			});
		},
		[order.travellers, order.additionalServices.insurances, saveOrderServices]
	);

	const saveOrderServicesRequest = useCallback(
		(codes: string[] = []) => {
			const selectedCodesSet = new Set([...codes, ...insuranceCodesToSave]);

			return saveOrderServicesWithParams([...selectedCodesSet]);
		},
		[insuranceCodesToSave, saveOrderServicesWithParams]
	);

	const handleAgree = useCallback(async () => {
		await insuranceUpsaleModal.onAgree(async insuranceCodes => {
			if (!insuranceCodes?.length) {
				return;
			}

			await saveOrderServicesRequest(insuranceCodes);
		});
	}, [saveOrderServicesRequest, insuranceUpsaleModal.onAgree]);

	const handleDisagree = useCallback(async () => {
		await insuranceUpsaleModal.onDisagree(async () => {
			if (!isNeedToSendSaveRequest) {
				return;
			}

			await saveOrderServicesRequest();
		});
	}, [isNeedToSendSaveRequest, saveOrderServicesRequest, insuranceUpsaleModal.onDisagree]);

	const saveOrderServicesCallback = useCallback(
		async (callback: () => void) => {
			if (isNeedSelectImportantInsurances) {
				onShowImportantInsurancesError();

				return;
			}

			await insuranceUpsaleModal.onContinue(async () => {
				if (isNeedToSendSaveRequest) {
					await saveOrderServicesRequest();
				}

				callback();
			});
		},
		[
			isNeedToSendSaveRequest,
			saveOrderServicesRequest,
			isNeedSelectImportantInsurances,
			insuranceUpsaleModal.onContinue
		]
	);

	const onDownloadNasInsurance = useCallback(async () => {
		const { data } = await getDocument({
			params: { orderId: order.id, documentType: OrderDocumentType.NASTicket }
		});

		if (data?.OrderDocument) {
			downloadPDF(data.OrderDocument, 'insurance.pdf');
		}
	}, [order.id]);

	return {
		// methods
		saveOrderServices,
		saveOrderServicesRequest,
		saveOrderServicesWithParams,
		saveOrderServicesCallback,
		setInsurancePriceToPay,
		setIsNeedToSendSaveReq,
		setSaveOrderServicesParams,
		onHideImportantInsurancesError,
		closeUpsaleModal: insuranceUpsaleModal.onCloseUpsaleModal,
		onContinue: insuranceUpsaleModal.onContinue,
		handleAgree,
		handleDisagree,
		onDownloadNasInsurance,

		// data
		order,
		orderId: order.id,
		orderPriceToPay,
		insurancePriceToPay,
		totalPriceToPay,
		mutationResult,
		saveOrderServicesParams,
		selectedSaveServicesCodes,
		priorityInsurance: insuranceUpsaleModal.priorityInsurance,
		isNeedToSendSaveRequest,
		isUpsaleOpen: insuranceUpsaleModal.isUpsaleOpen,
		isLoading: mutationResult.loading,
		isShowImportantInsurancesError
	};
};

function getStrategyKey(strategy: UpsaleStrategy, orderId: string) {
	return `${strategy}_${orderId}`;
}

export const useInsuranceUpsaleConfig = () => {
	const updateId = useRef<number>(0);
	const mode = useContext(ModeContext);
	const order = mode === Mode.Checkin ? useSelector(getCheckinOrder) : useSelector(getCheckoutOrder);

	const {
		global: {
			insurance: { upsaleStrategy }
		}
	} = useConfig();

	const [shownStrategies, unShownStrategies] = useMemo(() => {
		const shown: UpsaleStrategy[] = [];
		const unShown: UpsaleStrategy[] = [];

		upsaleStrategy.forEach(strategyKey => {
			const existKey = get<boolean>(`${strategyKey}_${order.id}`);

			if (existKey) {
				shown.push(strategyKey);
			} else {
				unShown.push(strategyKey);
			}
		});

		return [shown, unShown];
	}, [upsaleStrategy, order.id, updateId.current]);

	const getIsNeedToShow = useCallback(
		(strategy: UpsaleStrategy) => {
			return upsaleStrategy.includes(strategy) && unShownStrategies.includes(strategy);
		},
		[upsaleStrategy, unShownStrategies]
	);

	const onApplyStrategy = useCallback(
		(strategy: UpsaleStrategy) => {
			set(getStrategyKey(strategy, order.id), true);
			updateId.current++;
		},
		[order.id, updateId.current]
	);

	const onCancelStrategy = useCallback(
		(strategy: UpsaleStrategy) => {
			set(getStrategyKey(strategy, order.id), false);
			updateId.current++;
		},
		[order.id, updateId.current]
	);

	return {
		// data
		upsaleStrategy,
		shownStrategies,
		unShownStrategies,

		// methods
		getIsNeedToShow,
		onApplyStrategy,
		onCancelStrategy
	};
};

export const useImportantInsurances = ({
	availableInsuranceCodes = [],
	selectedInsuranceCodes = []
}: {
	availableInsuranceCodes?: string[];
	selectedInsuranceCodes?: string[];
} = {}) => {
	const mode = useContext(ModeContext);

	const orderId = mode === Mode.Checkin ? useSelector(getCheckinOrderId) : useSelector(getCheckoutOrderId);
	const importantInsuranceCodes =
		mode === Mode.Checkin
			? useSelector(getCheckinImportantInsuranceCodes(availableInsuranceCodes))
			: useSelector(getCheckoutImportantInsuranceCodes(availableInsuranceCodes));

	const importantInsurancesTouchedKey = `${IMPORTANT_INSURANCE_TOUCHED}_${orderId}`;
	const importantInsurancesTouchedCodes = sessionStorageGet<string[]>(importantInsurancesTouchedKey) ?? [];

	const isAllImportantInsurancesTouchedOrSelected = useMemo(() => {
		if (!importantInsuranceCodes.length) {
			return true;
		}

		return importantInsuranceCodes.every(
			code => importantInsurancesTouchedCodes.includes(code) || selectedInsuranceCodes.includes(code)
		);
	}, [importantInsuranceCodes, importantInsurancesTouchedCodes, selectedInsuranceCodes]);

	// Save in the session the parameter of the touched important
	// insurance to display pressed radio buttons in the SelectForm
	const onTouchInsurance = useCallback(
		(code: string) => {
			if (importantInsuranceCodes.includes(code)) {
				const codes = [...new Set([...importantInsurancesTouchedCodes, code])];

				sessionStorageSet(importantInsurancesTouchedKey, codes);
			}
		},
		[orderId, importantInsuranceCodes, importantInsurancesTouchedCodes]
	);

	const getIsTouched = useCallback((code: string) => importantInsurancesTouchedCodes.includes(code), [
		importantInsurancesTouchedCodes
	]);

	return {
		// data
		importantInsuranceCodes,
		importantInsurancesTouchedCodes,
		isAllImportantInsurancesTouchedOrSelected,

		// methods
		onTouchInsurance,
		getIsTouched
	};
};

export const useInsuranceRoute = ({
	goToNextStep,
	strategy
}: {
	goToNextStep: () => void;
	strategy: UpsaleStrategy;
}) => {
	const insurancePageWrapper = useInsurancePageWrapper({ strategy });

	const handleNextStep = async () => {
		await insurancePageWrapper.saveOrderServicesCallback(goToNextStep);
	};

	const handleAgree = async () => {
		if (insurancePageWrapper.priorityInsurance) {
			await insurancePageWrapper.handleAgree();
			goToNextStep();

			return;
		}

		insurancePageWrapper.handleAgree();
	};

	const handleDisagree = async () => {
		await insurancePageWrapper.handleDisagree();
		goToNextStep();
	};

	return {
		// methods
		handleNextStep,
		handleAgree,
		handleDisagree,
		setInsurancePriceToPay: insurancePageWrapper.setInsurancePriceToPay,
		setSaveOrderServicesParams: insurancePageWrapper.setSaveOrderServicesParams,
		setIsNeedToSendSaveReq: insurancePageWrapper.setIsNeedToSendSaveReq,
		onDownloadNasInsurance: insurancePageWrapper.onDownloadNasInsurance,

		// data
		insurancePriceToPay: insurancePageWrapper.insurancePriceToPay,
		orderPriceToPay: insurancePageWrapper.orderPriceToPay,
		totalPriceToPay: insurancePageWrapper.totalPriceToPay,
		priorityInsurance: insurancePageWrapper.priorityInsurance,
		isUpsaleOpen: insurancePageWrapper.isUpsaleOpen,
		isLoading: insurancePageWrapper.isLoading,
		isShowImportantInsurancesError: insurancePageWrapper.isShowImportantInsurancesError
	};
};
